if (!customElements.get('variant-ergiebigkeit')) {
  customElements.define(
    'variant-ergiebigkeit',
    class VariantErgiebigkeit extends HTMLElement {
      onVariantChangeUnsubscriber = undefined;
      variantMetafields = {};

      constructor() {
        super();
        this.init();
      }

      init() {
        // Load variant metafields data from JSON script
        const metafieldsScript = document.querySelector('[data-variant-metafields]');
        if (metafieldsScript) {
          try {
            this.variantMetafields = JSON.parse(metafieldsScript.textContent);
            console.log('Loaded variant metafields:', this.variantMetafields);
          } catch (error) {
            console.error('Error parsing variant metafields:', error);
          }
        }

        // Setup MutationObserver for script content changes
        this.setupScriptObserver();

        // Subscribe to variant change events
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleVariantChange.bind(this)
        );

        // Also listen directly to form and radio button changes
        this.setupDirectEventListeners();

        // Update display for initial variant
        this.updateErgiebigkeitDisplay();
      }

      disconnectedCallback() {
        if (this.onVariantChangeUnsubscriber) {
          this.onVariantChangeUnsubscriber();
        }
        
        // Clean up MutationObserver
        if (this.scriptObserver) {
          this.scriptObserver.disconnect();
          this.scriptObserver = null;
        }
        
        // Clean up value checker interval
        if (this.valueChecker) {
          clearInterval(this.valueChecker);
          this.valueChecker = null;
        }
        
        // Clean up direct event listeners
        if (this.directEventListeners) {
          this.directEventListeners.forEach(({element, handler}) => {
            element.removeEventListener('change', handler);
          });
          this.directEventListeners = [];
        }
      }

      setupScriptObserver() {
        // Watch for changes in the data-selected-variant script content
        const selectedVariantScript = document.querySelector('[data-selected-variant]');
        if (selectedVariantScript) {
          this.scriptObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === 'childList') {
                console.log('🔄 Script content changed, updating ergiebigkeit');
                // Small delay to ensure script is fully updated
                setTimeout(() => {
                  this.updateErgiebigkeitDisplay();
                }, 10);
              }
            });
          });
          
          this.scriptObserver.observe(selectedVariantScript, {
            childList: true,
            subtree: true,
            characterData: true
          });
          
          console.log('✓ Script observer setup complete');
        }
      }

      setupDirectEventListeners() {
        // Initialize event listeners array for cleanup
        this.directEventListeners = [];
        
        // Listen to form hidden input changes (most reliable)
        const hiddenInput = document.querySelector('input.product-variant-id');
        if (hiddenInput) {
          const hiddenHandler = () => {
            console.log('🔄 Form variant ID changed:', hiddenInput.value);
            this.updateErgiebigkeitDisplay();
          };
          
          hiddenInput.addEventListener('change', hiddenHandler);
          
          // Store original value for comparison
          let lastKnownValue = hiddenInput.value;
          
          // Poll for value changes as a backup (safer than property override)
          const valueChecker = setInterval(() => {
            if (hiddenInput.value !== lastKnownValue) {
              lastKnownValue = hiddenInput.value;
              console.log('🔄 Form value changed (polled):', hiddenInput.value);
              this.updateErgiebigkeitDisplay();
            }
          }, 100);
          
          // Store cleanup function
          this.valueChecker = valueChecker;
          this.directEventListeners.push({element: hiddenInput, handler: hiddenHandler});
        }
        
        // Listen to radio button changes directly for immediate feedback
        const radioButtons = document.querySelectorAll('input[type="radio"][name*="Gebinde"]');
        radioButtons.forEach(radio => {
          const handler = () => {
            console.log('🔄 Radio button changed:', radio.value);
            this.updateErgiebigkeitDisplay();
          };
          
          radio.addEventListener('change', handler);
          this.directEventListeners.push({element: radio, handler});
        });
      }

      handleVariantChange() {
        // Update ergiebigkeit display when variant changes via pub/sub
        // No timeout needed as MutationObserver and form listeners handle timing
        console.log('🔄 PubSub variant change event received');
        this.updateErgiebigkeitDisplay();
      }

      getCurrentSelectedVariantId() {
        let variantId = null;
        
        // Method 1: Form hidden input (most reliable for current selection)
        const hiddenInput = document.querySelector('input.product-variant-id');
        if (hiddenInput && hiddenInput.value) {
          variantId = hiddenInput.value;
          console.log('🎯 Method 1: Form hidden input:', variantId);
          
          // Cross-validate with radio button
          const checkedRadio = document.querySelector('input[type="radio"][name*="Gebinde"]:checked');
          if (checkedRadio) {
            console.log('✓ Cross-validation: Radio option:', checkedRadio.value);
            return variantId;
          }
        }
        
        // Method 2: data-selected-variant script (fallback)
        const selectedVariantScript = document.querySelector('[data-selected-variant]');
        if (selectedVariantScript) {
          try {
            const selectedVariant = JSON.parse(selectedVariantScript.textContent);
            variantId = selectedVariant?.id?.toString();
            
            if (variantId) {
              console.log('🎯 Method 2: Script variant:', variantId);
              
              // Verify with radio button selection
              const checkedRadio = document.querySelector('input[type="radio"][name*="Gebinde"]:checked');
              if (checkedRadio && selectedVariant.options) {
                const selectedValue = checkedRadio.value;
                if (selectedVariant.options.includes(selectedValue)) {
                  console.log('✓ Script and radio match - Option:', selectedValue);
                  return variantId;
                } else {
                  console.warn('⚠ MISMATCH! Radio:', selectedValue, 'Script options:', selectedVariant.options);
                  // Trust the hidden input over the script if we had one from Method 1
                  const hiddenInputValue = document.querySelector('input.product-variant-id')?.value;
                  if (hiddenInputValue && hiddenInputValue !== variantId) {
                    console.log('🔧 Using hidden input over mismatched script:', hiddenInputValue);
                    return hiddenInputValue;
                  }
                  return variantId;
                }
              }
              
              return variantId;
            }
          } catch (error) {
            console.error('Error parsing selected variant script:', error);
          }
        }
        
        // Method 3: Manual mapping from radio button (last resort)
        const checkedRadio = document.querySelector('input[type="radio"][name*="Gebinde"]:checked');
        if (checkedRadio) {
          const optionValue = checkedRadio.value;
          console.log('🎯 Method 3: Manual mapping for radio:', optionValue);
          
          // Try to find variant ID by matching option values in metafields data
          for (const [candidateId] of Object.entries(this.variantMetafields)) {
            // This is a fallback method - we'll use the script method above primarily
            console.log('Last resort: checking variant', candidateId, 'for option', optionValue);
          }
        }
        
        console.error('❌ No method to determine current variant ID');
        return null;
      }

      updateErgiebigkeitDisplay() {
        const variantId = this.getCurrentSelectedVariantId();
        
        if (!variantId) {
          console.log('No variant ID found, hiding ergiebigkeit info');
          this.hideErgiebigkeitInfo();
          return;
        }
        
        if (!this.variantMetafields[variantId]) {
          console.log('No metafields found for variant:', variantId);
          this.hideErgiebigkeitInfo();
          return;
        }

        const metafields = this.variantMetafields[variantId];
        console.log('✓ Showing ergiebigkeit for variant:', variantId, metafields);
        
        // Check if either ergiebigkeit field has content
        if (metafields.ergiebigkeit_1 || metafields.ergiebigkeit_2) {
          this.showErgiebigkeitInfo(metafields.ergiebigkeit_1, metafields.ergiebigkeit_2);
        } else {
          console.log('No ergiebigkeit data for variant:', variantId);
          this.hideErgiebigkeitInfo();
        }
      }

      showErgiebigkeitInfo(ergiebigkeit1, ergiebigkeit2) {
        const container = this.querySelector('.variant-ergiebigkeit');
        if (!container) return;

        // Build display text
        let displayText = '';
        if (ergiebigkeit1 && ergiebigkeit2) {
          displayText = `${ergiebigkeit1} (${ergiebigkeit2})`;
        } else if (ergiebigkeit1) {
          displayText = ergiebigkeit1;
        } else if (ergiebigkeit2) {
          displayText = `(${ergiebigkeit2})`;
        }

        // Update or create the info display
        let infoElement = container.querySelector('.ergiebigkeit-info');
        if (!infoElement) {
          infoElement = document.createElement('div');
          infoElement.className = 'ergiebigkeit-info';
          container.appendChild(infoElement);
        }

        infoElement.innerHTML = `
          <span class="ergiebigkeit-values">${displayText}</span>
        `;

        container.style.display = 'block';
      }

      hideErgiebigkeitInfo() {
        const container = this.querySelector('.variant-ergiebigkeit');
        if (container) {
          container.style.display = 'none';
        }
      }
    }
  );
}

// Initialize for existing elements
document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('.product-variant-info').forEach(element => {
    if (!element.tagName || element.tagName.toLowerCase() !== 'variant-ergiebigkeit') {
      // Convert existing div to custom element
      const customElement = document.createElement('variant-ergiebigkeit');
      customElement.innerHTML = element.innerHTML;
      customElement.className = element.className;
      customElement.id = element.id;
      element.parentNode.replaceChild(customElement, element);
    }
  });
});